// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    highAvailability,
    IHighAvailabilityActivityRequest,
    IHighAvailabilityActivity,
    IHighAvailabilityActivityEntity,
    IHighAvailabilityActivityMoveBackRequest,
    IUpdateHighAvailabilityActivityEntity,
} from 'common/api/resources/HighAvailability';
import { Dispatch } from 'redux';
import {
    setIsLoading,
    unsetIsLoading,
} from 'common/modules/app/loadingFlags/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { HTTP_CODES } from 'common/api/constants';
import { bakeForegroundToast } from 'common/modules/app/toaster/actions';
import { INTENT_TYPE } from 'common/constants';
import { createCustomAction } from 'typesafe-actions';
import * as types from 'common/modules/highAvailability/constants/types';
import { IPaginateApiResponse } from 'common/api/resources/Response';
import { ICommonState } from 'common/store';
import { loadOnScroll } from 'common/actions/actionsWrapper';

export const setHighAvailabilities = createCustomAction(
    types.SET_HA_ACTIVITIES,
    (data: IPaginateApiResponse<IHighAvailabilityActivity[]>) => ({ payload: data })
);

export const setHighAvailabilityEntitiesForActivity = createCustomAction(
    types.SET_HA_PROCESSES_FOR_ACTIVITY,
    (data: { data: IPaginateApiResponse<IHighAvailabilityActivityEntity[]>; activityId: number }) => ({ payload: data })
);

export const setHighAvailabilityLoading = createCustomAction(
    types.SET_HA_ACTIVITY_LOADING,
    (id: number) => ({ payload: id })
);

export const unsetHighAvailabilityLoading = createCustomAction(
    types.UNSET_HA_ACTIVITY_LOADING,
    (id: number) => ({ payload: id })
);

export const appendHighAvailabilityEntities = createCustomAction(
    types.APPEND_HA_PROCESSES,
    (data: { data: IPaginateApiResponse<IHighAvailabilityActivityEntity[]>; activityId: number }) => ({ payload: data })
);

export const updateHighAvailabilityEntity = createCustomAction(
    types.UPDATE_HA_PROCESS,
    (event: IUpdateHighAvailabilityActivityEntity) => ({ payload: event })
);

export const updateHighAvailabilityEntityTask = createCustomAction(
    types.UPDATE_HA_ENTITY_TASK,
    (
        taskId: number,
        progress: number,
        status: string,
        computeResourceVmId: number | null,
        computeResourceId: number | null,
        action: string
    ) => ({ payload: { taskId, progress, status, computeResourceVmId, computeResourceId, action } })
);

export const setHighAvailabilityEntityLoading = createCustomAction(
    types.SET_HA_PROCESS_LOADING,
    (id: number) => ({ payload: id })
);

export const unsetHighAvailabilityEntityLoading = createCustomAction(
    types.UNSET_HA_PROCESS_LOADING,
    (id: number) => ({ payload: id })
);

export const setHighAvailabilityEntityMoveBackPending = createCustomAction(
    types.SET_HA_ENTITY_MOVE_BACK_PENDING,
    (request: IHighAvailabilityActivityMoveBackRequest) => ({ payload: request })
);

export const unsetHighAvailabilityEntityMoveBackPending = createCustomAction(
    types.UNSET_HA_ENTITY_MOVE_BACK_PENDING,
    (request: IHighAvailabilityActivityMoveBackRequest) => ({ payload: request })
);

export const getHighAvailabilities = (request?: IHighAvailabilityActivityRequest) => async(dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.HIGH_AVAILABILITY_HISTORY));

    try {
        const result = await highAvailability.activities.list(request);
        if (result.status === HTTP_CODES.OK) {
            dispatch(setHighAvailabilities(result.data));
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.HIGH_AVAILABILITY_HISTORY));
    }
};

export const getHighAvailabilityEntitiesForActivity = (id: number, perPage: number = 50) => async(dispatch: Dispatch) => {
    dispatch(setHighAvailabilityLoading(id));

    try {
        const result = await highAvailability.entities.list(id, { per_page: perPage });
        if (result.status === HTTP_CODES.OK) {
            dispatch(setHighAvailabilityEntitiesForActivity({
                data: result.data,
                activityId: id,
            }));
        }

        return result;
    } finally {
        dispatch(unsetHighAvailabilityLoading(id));
    }
};

export const loadMoreHighAvailabilityEntitiesOnScroll = (activity: IHighAvailabilityActivity) => async (dispatch: Dispatch, getState: () => ICommonState) => {
    const state = getState();
    const isLoading = state.app.loadingFlags.has(LOADING_FLAGS.HIGH_AVAILABILITY_ENTITIES_INFINITY_SCROLL);
    const nextPage = activity.entities?.links.next;
    if (!nextPage) {
        return;
    }

    return await loadOnScroll({
        nextPage,
        isLoading,
        dispatch,
        action: (param: IPaginateApiResponse<IHighAvailabilityActivityEntity[]>) => appendHighAvailabilityEntities({
            data: param, activityId: activity.id,
        }),
        loadingFlag: LOADING_FLAGS.HIGH_AVAILABILITY_ENTITIES_INFINITY_SCROLL,
    });
};

export const moveBackHighAvailabilityEntities = (request: IHighAvailabilityActivityMoveBackRequest) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.HIGH_AVAILABILITY_MOVING_BACK));
    dispatch(setHighAvailabilityEntityMoveBackPending(request));
    (request.entity_ids ?? []).forEach(id => dispatch(setHighAvailabilityEntityLoading(id)));

    try {
        const result = await highAvailability.entities.moveBack(request);
        if (result.status === HTTP_CODES.OK) {
            bakeForegroundToast(INTENT_TYPE.SUCCESS, 'highAvailability.activities.operations.moveBackStarted')(dispatch);
        }
        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.HIGH_AVAILABILITY_MOVING_BACK));
        dispatch(unsetHighAvailabilityEntityMoveBackPending(request));
        (request.entity_ids ?? []).forEach(id => dispatch(unsetHighAvailabilityEntityLoading(id)));
    }
};
